import { supabase } from './supabaseClient'
import type { BillingInterval } from './billingInterval'
import type { CheckoutPlan } from './planTier'
import { getUserFriendlyMessage, getUserFriendlyMessageFromText } from './errors'
import { captureError } from './sentry'

type BillingRedirectResponse = {
  url?: string
  error?: string
}

type CheckoutRequest = {
  plan: CheckoutPlan
  interval?: BillingInterval
}

function extractRedirectUrl(data: unknown): string {
  const payload = data as BillingRedirectResponse | null
  if (!payload?.url) {
    throw new Error(getUserFriendlyMessageFromText(payload?.error ?? 'Billing redirect unavailable'))
  }
  return payload.url
}

async function handleInvokeError(error: unknown, functionName: string, context: string): Promise<never> {
  captureError(error, { function: functionName, context })
  throw new Error(await getUserFriendlyMessage(error))
}

export async function startCheckout(plan: CheckoutPlan, interval: BillingInterval = 'monthly'): Promise<string> {
  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: { plan, interval } satisfies CheckoutRequest,
  })
  if (error) await handleInvokeError(error, 'create-checkout-session', 'start_checkout')
  return extractRedirectUrl(data)
}

export async function openBillingPortal(): Promise<string> {
  const { data, error } = await supabase.functions.invoke('create-portal-session')
  if (error) await handleInvokeError(error, 'create-portal-session', 'open_billing_portal')
  return extractRedirectUrl(data)
}

export async function redirectToCheckout(
  plan: CheckoutPlan,
  interval: BillingInterval = 'monthly',
): Promise<void> {
  const url = await startCheckout(plan, interval)
  window.location.assign(url)
}

export async function redirectToBillingPortal(): Promise<void> {
  const url = await openBillingPortal()
  window.location.assign(url)
}

export async function confirmCheckoutSession(sessionId: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('confirm-checkout-session', {
    body: { session_id: sessionId },
  })
  if (error) await handleInvokeError(error, 'confirm-checkout-session', 'confirm_checkout')
  const payload = data as { error?: string; ok?: boolean } | null
  if (payload?.error) throw new Error(getUserFriendlyMessageFromText(payload.error))
  if (!payload?.ok) throw new Error(getUserFriendlyMessageFromText('Checkout confirmation failed'))
}

export async function syncStripeBilling(): Promise<{ synced: boolean; plan: string | null }> {
  const { data, error } = await supabase.functions.invoke('sync-stripe-billing')
  if (error) await handleInvokeError(error, 'sync-stripe-billing', 'restore_subscription')
  const payload = data as { error?: string; synced?: boolean; plan?: string | null } | null
  if (payload?.error) throw new Error(getUserFriendlyMessageFromText(payload.error))
  if (!payload?.synced) {
    throw new Error(getUserFriendlyMessageFromText('Billing sync did not update your subscription'))
  }
  return { synced: true, plan: payload.plan ?? null }
}

/** @deprecated Use redirectToCheckout('pro') or redirectToCheckout('pro_plus') */
export async function redirectToPremiumCheckout(): Promise<void> {
  await redirectToCheckout('pro')
}
